import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, LogIn, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiError } from "@/lib/auth";
import { Input } from "@/components/ui/input";

export default function AdminLogin() {
  const nav = useNavigate();
  const { user, login, checking } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (!checking && user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Enter your email and password.");
      return;
    }
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Welcome back.");
      nav("/admin", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="admin-login-page" className="min-h-screen flex items-center justify-center px-6 py-24 bg-bone">
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md bg-white border border-ink/15 p-8 md:p-10"
      >
        <Link to="/" data-testid="admin-back-home" className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.24em] text-ink/60 hover:text-ink">
          <ArrowLeft className="w-4 h-4" /> Back to site
        </Link>
        <div className="mt-6 flex items-center gap-3 text-[11px] uppercase tracking-[0.32em] text-ink/50">
          <Lock className="w-4 h-4 text-moss" /> Velixa Health · Admin
        </div>
        <h1 className="mt-3 font-serif text-4xl tracking-tightest text-ink leading-[1]">Private <span className="italic">admin</span>.</h1>
        <p className="mt-3 text-[14px] text-ink/60">Sign in to view enquiries and export the leads CSV.</p>

        <form onSubmit={submit} className="mt-8 space-y-5">
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.28em] text-ink/55 mb-2">Email</div>
            <Input
              data-testid="admin-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@velixahealth.com"
              className="h-12 rounded-sm bg-transparent border-ink/20 focus-visible:ring-ink"
              autoComplete="username"
            />
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.28em] text-ink/55 mb-2">Password</div>
            <Input
              data-testid="admin-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="h-12 rounded-sm bg-transparent border-ink/20 focus-visible:ring-ink"
              autoComplete="current-password"
            />
          </label>

          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-3 px-6 py-4 bg-ink text-bone text-[12px] uppercase tracking-[0.24em] hover:bg-moss disabled:opacity-60 transition-colors"
          >
            {loading ? "Signing in…" : (<><LogIn className="w-4 h-4" /> Sign in</>)}
          </button>
        </form>

        <p className="mt-8 text-[11px] uppercase tracking-[0.22em] text-ink/45">Access is logged. Do not share credentials.</p>
      </motion.div>
    </main>
  );
}
